/* ==========================================================================
   useChangelog Hook
   Fetches changelog data and tracks whether user has seen the latest version
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface ChangelogVersion {
  version: string
  date: string
  title: string
  tag: 'feature' | 'improvement' | 'fix'
  providerHighlights: string[]
  adminHighlights: string[]
  details: string | null
}

interface ChangelogData {
  versions: ChangelogVersion[]
}

interface UseChangelogResult {
  versions: ChangelogVersion[]
  hasNew: boolean
  markSeen: () => void
  loading: boolean
}

/* --------------------------------------------------------------------------
   Module-level cache
   -------------------------------------------------------------------------- */

const STORAGE_KEY = 'tjm_changelog_last_seen'
let cachedData: ChangelogData | null = null

/* --------------------------------------------------------------------------
   Hook
   -------------------------------------------------------------------------- */

export function useChangelog(): UseChangelogResult {
  const [versions, setVersions] = useState<ChangelogVersion[]>(cachedData?.versions ?? [])
  const [loading, setLoading] = useState(cachedData === null)
  const [hasNew, setHasNew] = useState(false)

  useEffect(() => {
    if (cachedData) {
      setVersions(cachedData.versions)
      setLoading(false)
      checkHasNew(cachedData.versions)
      return
    }

    let cancelled = false

    async function fetchChangelog() {
      try {
        const response = await fetch('/data/changelog.json')
        if (!response.ok) return
        const data: ChangelogData = await response.json()
        cachedData = data
        if (!cancelled) {
          setVersions(data.versions)
          checkHasNew(data.versions)
        }
      } catch {
        // Silently fail — changelog indicator should be invisible if data isn't available
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchChangelog()
    return () => { cancelled = true }
  }, [])

  function checkHasNew(versionList: ChangelogVersion[]) {
    if (versionList.length === 0) return
    const lastSeen = localStorage.getItem(STORAGE_KEY)
    setHasNew(lastSeen !== versionList[0].version)
  }

  const markSeen = useCallback(() => {
    if (versions.length === 0) return
    localStorage.setItem(STORAGE_KEY, versions[0].version)
    setHasNew(false)
  }, [versions])

  return { versions, hasNew, markSeen, loading }
}
